import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import Layout from './components/Layout';
import Menu from './components/Menu';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import CookieBanner from './components/CookieBanner';
import CookieReopenButton from './components/CookieReopenButton';
import { getStoredConsent, saveConsent, logConsentToFirebase } from './lib/cookieConsent';

const GOOGLE_FONTS_LINK_ID = 'duomila-google-fonts';

const loadGoogleFonts = () => {
  if (document.getElementById(GOOGLE_FONTS_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = GOOGLE_FONTS_LINK_ID;
  link.href =
    'https://fonts.googleapis.com/css2?family=Faculty+Glyphic&family=Noto+Sans:wght@500&family=Nunito&display=swap';
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

const unloadGoogleFonts = () => {
  const link = document.getElementById(GOOGLE_FONTS_LINK_ID);
  if (link) link.parentNode.removeChild(link);
};

function App() {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored && typeof stored === 'object' && stored.action) {
      if (stored.action === 'accept_all') {
        loadGoogleFonts();
      }
      setHasConsent(true);
      return;
    }

    const schedule =
      typeof window !== 'undefined' && 'requestIdleCallback' in window
        ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
        : (cb) => setTimeout(cb, 1500);
    const handle = schedule(() => setBannerOpen(true));
    return () => {
      if (typeof window !== 'undefined' && 'cancelIdleCallback' in window && handle) {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);

  const finalizeConsent = useCallback((consent) => {
    saveConsent(consent);
    setHasConsent(true);
    setBannerOpen(false);
    logConsentToFirebase(consent);
  }, []);

  const handleAcceptAll = useCallback(() => {
    loadGoogleFonts();
    finalizeConsent({ analytics: false, marketing: false, action: 'accept_all' });
  }, [finalizeConsent]);

  const handleRejectAll = useCallback(() => {
    unloadGoogleFonts();
    finalizeConsent({ analytics: false, marketing: false, action: 'reject_all' });
  }, [finalizeConsent]);

  const handleReopen = useCallback(() => {
    setBannerOpen(true);
  }, []);

  return (
    <BrowserRouter>
      <CookieBanner
        open={bannerOpen}
        onAcceptAll={handleAcceptAll}
        onRejectAll={handleRejectAll}
      />
      <CookieReopenButton visible={hasConsent && !bannerOpen} onClick={handleReopen} />

      <Layout>
        <Routes>
          <Route path="/" element={<Menu />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
